import { getBike, MY_BIKE_EVENT } from '@theme/my-bike';

/**
 * Home "For your <model>" row (sections/voge-bike-picks.liquid).
 * When a bike is saved, loads the top products of its collection through the Section Rendering API
 * (/collections/<handle>?section_id=voge-bike-picks) and shows the row; hidden otherwise.
 * Reloads when the bike changes (header selector, bike finder, other tabs).
 */
class BikePicks extends HTMLElement {
  #controller = new AbortController();
  /** @type {AbortController | null} */
  #fetchController = null;
  #loadedFor = '';

  connectedCallback() {
    document.addEventListener(MY_BIKE_EVENT, () => this.#load(), { signal: this.#controller.signal });
    this.#load();
  }

  disconnectedCallback() {
    this.#controller.abort();
    this.#fetchController?.abort();
  }

  async #load() {
    const bike = getBike();
    if (!bike?.url) {
      this.#loadedFor = '';
      this.hidden = true;
      return;
    }
    if (bike.url === this.#loadedFor) return;
    this.#loadedFor = bike.url;

    this.#fetchController?.abort();
    const controller = new AbortController();
    this.#fetchController = controller;

    try {
      const url = new URL(bike.url, window.location.origin);
      url.searchParams.set('section_id', this.dataset.section || 'voge-bike-picks');
      const response = await fetch(url.toString(), { signal: controller.signal, credentials: 'same-origin' });
      if (!response.ok) throw new Error(String(response.status));
      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      const list = doc.querySelector('[data-picks-items]');
      if (controller.signal.aborted) return;
      if (!list || !list.querySelector('li')) {
        this.hidden = true;
        return;
      }

      const heading = this.querySelector('[data-picks-heading]');
      if (heading) heading.textContent = (this.dataset.heading || '[model]').replace('[model]', bike.title);
      const link = /** @type {HTMLAnchorElement | null} */ (this.querySelector('[data-picks-link]'));
      if (link) {
        link.href = bike.url;
        link.textContent = (this.dataset.link || '[model]').replace('[model]', bike.title);
      }
      this.querySelector('[data-picks-list]')?.replaceChildren(document.importNode(list, true));
      this.hidden = false;
    } catch (error) {
      if (/** @type {Error} */ (error).name !== 'AbortError') {
        this.#loadedFor = '';
        this.hidden = true;
      }
    }
  }
}

if (!customElements.get('bike-picks')) customElements.define('bike-picks', BikePicks);
